/**
 * Labstep
 *
 * @module components/Molecule/Hazards
 * @desc Showing molecule hazards
 */

import classnames from 'classnames';
import { MoleculeSafetyActionAdd as ActionAdd } from 'labstep-web/components/Molecule/Safety/ActionAdd';
import { MoleculeSafetyActionRemove as ActionRemove } from 'labstep-web/components/Molecule/Safety/ActionRemove';
import { MoleculeSafetyHazards as Hazards } from 'labstep-web/components/Molecule/Safety/Hazards';
import { MoleculeSafetyPictograms as Pictograms } from 'labstep-web/components/Molecule/Safety/Pictograms';
import { MoleculeSafetyPrecautionary as Precautionary } from 'labstep-web/components/Molecule/Safety/Precautionary';
import { withAuthenticatedUserEntity } from 'labstep-web/containers/AuthenticatedUser';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { Header } from 'labstep-web/core/Header';
import { Hoverable } from 'labstep-web/core/Hoverable';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import { Metadata } from 'labstep-web/models/metadata';
import type React from 'react';
import styles from './styles.module.scss';
import type {
  MoleculeSafetyCodeListProps,
  MoleculeSafetyProps,
} from './types';

export const MoleculeSafetyCodeList: React.FC<
  MoleculeSafetyCodeListProps
> = ({ label, codes, parent, type, subtype, pubChemMetadata }) => (
  <>
    <div>
      <Header children={label} size="mini" />
      <ul className={styles.codeList}>
        {codes.map((code) => (
          <li key={code.code}>
            <Hoverable
              hoverable={
                <div>
                  <u>{code.code}</u>
                  {code.info && ` - ${code.info}`}
                </div>
              }
              component={
                parent && (
                  <ActionRemove
                    parent={parent}
                    type={type}
                    code={code.code}
                    subtype={subtype}
                    pubChemMetadata={pubChemMetadata}
                  />
                )
              }
            />
          </li>
        ))}
      </ul>
      {parent && (
        <ActionAdd
          pubChemMetadata={pubChemMetadata}
          parent={parent}
          type={type}
          subtype={subtype}
        />
      )}
    </div>
    <br />
  </>
);

export const MoleculeSafety: React.FC<MoleculeSafetyProps> = ({
  pubChemMetadata,
  parent,
  inline,
  modalProps,
  metadataId,
}) => {
  if (!pubChemMetadata.Safety || !parent) {
    return null;
  }

  return (
    <ModalDefault
      header="Safety Information"
      content={(): React.ReactElement =>
        metadataId ? (
          <ReadOnMountHOC
            type="entities"
            entityName={Metadata.entityName}
            params={{
              id: metadataId,
              get_single: 1,
            }}
            loading={{ loader: 'spinner' }}
            children={({ entity: metadata }) => {
              return (
                <div>
                  <Pictograms
                    parent={metadata.molecule}
                    pubChemMetadata={metadata.molecule.properties}
                  />
                  <Hazards
                    parent={metadata.molecule}
                    pubChemMetadata={metadata.molecule.properties}
                  />
                  <Precautionary
                    parent={metadata.molecule}
                    pubChemMetadata={metadata.molecule.properties}
                  />
                </div>
              );
            }}
          />
        ) : (
          <div>
            <Pictograms
              parent={parent}
              pubChemMetadata={pubChemMetadata}
            />
            <Hazards
              parent={parent}
              pubChemMetadata={pubChemMetadata}
            />
            <Precautionary
              parent={parent}
              pubChemMetadata={pubChemMetadata}
            />
          </div>
        )
      }
      viewComponent={({ toggleModal }): React.ReactElement => (
        <div
          className={classnames(styles.container, {
            [styles.inline]: inline,
          })}
        >
          <Pictograms
            pubChemMetadata={pubChemMetadata}
            isInline
            size={inline ? 'mini' : undefined}
          />
          <ActionComponent
            onClick={toggleModal}
            type="text"
            elementProps={{
              underline: true,
            }}
            text="show all codes"
          />
        </div>
      )}
      {...modalProps}
    />
  );
};

export default withAuthenticatedUserEntity(MoleculeSafety);
